import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModelType, IImageGeneration, PaginationResult, ImageGenerationQuery, PaginationQuery, ImageGenerationStatus, ImageGenerationType, Resolution, AspectRatio } from '@packages/global';
import { imageGenerationApi } from '../services/user';
import { useModels } from '../hooks/useModels';
import Pagination from '../components/Pagination';
import ImageGenerationCard from '../components/ImageGeneration/ImageGenerationCard';
import SearchBar from '../components/ImageGeneration/SearchBar';
import ImageGenerationForm from '../components/ImageGeneration/ImageGenerationForm';
import { useDialog } from '../hooks/useDialog';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType, TaskStatus } from '../services/websocket/types';
import '../styles/line-clamp.css';

interface ImageGenerationFormData {
  prompt: string;
  modelId: string;
  resolution: Resolution;
  aspectRatio: AspectRatio;
  referenceImageUrl?: string;
}

export default function ImageGenerationsPage() {
  const { t } = useTranslation(['generation', 'common']);
  const [items, setItems] = useState<IImageGeneration[]>([]);
  const { models, loading: modelsLoading } = useModels(ModelType.TEXT_TO_IMAGE);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<IImageGeneration | null>(null);
  const { alert, confirm } = useDialog();
  const { client, isConnected } = useWebSocketContext();
  const [formData, setFormData] = useState<ImageGenerationFormData>({
    prompt: '',
    modelId: '',
    resolution: Resolution.HD_720P,
    aspectRatio: AspectRatio.LANDSCAPE,
    referenceImageUrl: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ImageGenerationStatus | ''>('');
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1,
    limit: 24,
    total: 0,
    totalPages: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const loadItems = async (
    queryParams: Partial<ImageGenerationQuery> = {},
    page: number = 1,
    limit: number = 24
  ) => {
    try {
      const queryData: PaginationQuery<ImageGenerationQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          status: queryParams.status ?? (statusFilter || undefined),
          imageType: ImageGenerationType.GENERATION,
          ...queryParams
        },
        pagination: {
          page,
          limit,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      };
      const response = await imageGenerationApi.imageGenerationPageList(queryData);
      setItems(response.items);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('加载图片生成任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  // 🔥 页面级别订阅 WebSocket，监听所有图片生成任务的状态更新
  useEffect(() => {
    if (!client || !isConnected) {
      return;
    }

    // 监听状态变化消息
    const unsubscribeStatusChanged = client.onMessage(
      MessageType.TASK_STATUS_CHANGED,
      (message: TaskStatusChangedMessage) => {
        if (message.taskType !== TaskType.IMAGE_GENERATION) return;

        const statusMap: Record<TaskStatus, ImageGenerationStatus> = {
          [TaskStatus.PENDING]: ImageGenerationStatus.PENDING,
          [TaskStatus.SUBMITTED]: ImageGenerationStatus.PENDING,
          [TaskStatus.PROCESSING]: ImageGenerationStatus.PROCESSING,
          [TaskStatus.COMPLETED]: ImageGenerationStatus.COMPLETED,
          [TaskStatus.FAILED]: ImageGenerationStatus.FAILED
        };

        const newStatus = statusMap[message.newStatus];
        if (!newStatus) return;

        setItems(prevItems => {
          const itemIndex = prevItems.findIndex(item => item.id === message.taskId);
          if (itemIndex === -1) return prevItems;

          const newItems = [...prevItems];
          newItems[itemIndex] = { ...newItems[itemIndex], status: newStatus };

          if (newStatus === ImageGenerationStatus.COMPLETED || newStatus === ImageGenerationStatus.FAILED) {
            imageGenerationApi.getImageGeneration(message.taskId)
              .then(updatedItem => {
                setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item));
              })
              .catch(console.error);
          }

          return newItems;
        });
      }
    );

    const unsubscribeCompleted = client.onMessage(MessageType.TASK_COMPLETED, (message: any) => {
      if (message.taskType !== TaskType.IMAGE_GENERATION) return;
      imageGenerationApi.getImageGeneration(message.taskId)
        .then(updatedItem => setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item)))
        .catch(console.error);
    });

    const unsubscribeFailed = client.onMessage(MessageType.TASK_FAILED, (message: any) => {
      if (message.taskType !== TaskType.IMAGE_GENERATION) return;
      imageGenerationApi.getImageGeneration(message.taskId)
        .then(updatedItem => setItems(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item)))
        .catch(console.error);
    });

    return () => {
      unsubscribeStatusChanged();
      unsubscribeCompleted();
      unsubscribeFailed();
    };
  }, [client, isConnected]);

  // 当模型加载完成且表单中没有选择模型时，自动选择第一个模型
  useEffect(() => {
    if (!modelsLoading && !formData.modelId && models.length > 0) {
      setFormData(prev => ({ ...prev, modelId: models[0].id }));
    }
  }, [models, modelsLoading, formData.modelId]);

  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    loadItems({ search: term, status: statusFilter || undefined }, 1);
  };

  const handleStatusFilterChange = (status: ImageGenerationStatus | '') => {
    setStatusFilter(status);
    loadItems({ search: searchTerm, status: status || undefined }, 1);
  };

  const handleAspectRatioChange = (ratio: AspectRatio) => {
    setFormData(prev => ({
      ...prev,
      aspectRatio: ratio
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.modelId) {
      await alert(t('generation:form.selectModel'), t('common:info'), 'warning');
      return;
    }
    try {
      // 总是创建新任务，不再有编辑逻辑
      const newTask = await imageGenerationApi.createImageGeneration({
        ...formData,
        imageType: ImageGenerationType.GENERATION
      });
      // 🔥 直接把新任务加入列表头部
      setItems(prevItems => [newTask, ...prevItems]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));
      handleCloseForm();
    } catch (error) {
      console.error('操作失败:', error);
    }
  };

  const handleItemUpdate = (updatedItem: IImageGeneration) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === updatedItem.id ? updatedItem : item
      )
    );
  };

  const handleEdit = (item: IImageGeneration) => {
    // 基于当前项创建新任务
    setEditingItem(item);
    setFormData({
      prompt: item.prompt,
      modelId: item.modelId,
      resolution: item.resolution,
      aspectRatio: item.aspectRatio,
      referenceImageUrl: item.referenceImageUrl || ''
    });
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm(t('generation:image.deleteConfirm'), t('common:deleteConfirmTitle'), 'danger');
    if (confirmed) {
      try {
        await imageGenerationApi.deleteImageGeneration(id);
        loadItems();
      } catch (error) {
        console.error('删除失败:', error);
      }
    }
  };

  const handleCloseForm = () => {
    setEditingItem(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      prompt: '',
      modelId: models.length > 0 ? models[0].id : '',
      resolution: Resolution.HD_720P,
      aspectRatio: AspectRatio.SQUARE,
      referenceImageUrl: ''
    });
  };

  const handleFormDataChange = (data: Partial<ImageGenerationFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  return (
    <div className="flex h-screen bg-bg-page">
      {/* 左侧表单 */}
      <ImageGenerationForm
        editingItem={editingItem}
        formData={formData}
        models={models}
        modelsLoading={modelsLoading}
        onSubmit={handleSubmit}
        onFormDataChange={handleFormDataChange}
        onAspectRatioChange={handleAspectRatioChange}
        onReset={handleCloseForm}
      />

      {/* 右侧内容区域 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 页面标题 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-50">{t('generation:image.title')}</h1>
              <p className="mt-1 text-sm text-gray-400">{t('generation:image.subtitle')}</p>
            </div>
            <SearchBar
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              onSearchTermChange={handleSearchTermChange}
              onStatusFilterChange={handleStatusFilterChange}
            />
          </div>

          {/* 卡片网格 */}
          {loading ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-12">
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-400">{t('common:loading')}</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-12">
              <div className="text-center">
                <p className="text-gray-400">{t('generation:image.empty')}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,240px)] justify-between gap-x-4 gap-y-6">
              {items.map((item) => (
                <ImageGenerationCard
                  key={item.id}
                  item={item}
                  models={models}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onUpdate={handleItemUpdate}
                />
              ))}
            </div>
          )}

          {/* 分页组件 */}
          {pagination.totalPages > 1 && (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-4 mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => loadItems({}, page, pagination.limit)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}